import { useCallback, useReducer } from "react";
import { DefaultTagAssociationTypeEnum, IObjectDefaultsModel, IObjectTagAssociationTypeDefaultModel } from "../../services/defaults-data-service/Models";
import storageService from "../../services/storage/storage-service/StorageService";
import { CheckListItemType } from "../check-list/CheckList";
import ObjectDefaultUtils from "./ObjectDefaultUtils";

const OBJECT_DEFAULTS_VIEW_OPTION_STORAGE_KEY = "ObjectDefaultsViewOptionsState";

export type ObjectDefaultsViewOptionsStateType = {
    mode: DefaultTagAssociationTypeEnum;
    tatsFilterValue: string;
    viewSelected: boolean;
}

export type ObjectDefaultsStateType = {
    availableTats: Array<CheckListItemType>;
    defaults: Array<IObjectTagAssociationTypeDefaultModel>;
    initialDefaults: Array<IObjectTagAssociationTypeDefaultModel>;
    options: ObjectDefaultsViewOptionsStateType;
    isDirty: boolean;
    isOptionsDirty: boolean;
}

let defaultObjectDefaultsState: ObjectDefaultsStateType = {
    availableTats: [],
    defaults: [],
    initialDefaults: [],
    options: storageService.local.get(OBJECT_DEFAULTS_VIEW_OPTION_STORAGE_KEY, {
        mode: DefaultTagAssociationTypeEnum.Graph,
        tatsFilterValue: "",
        viewSelected: false
    }),
    isDirty: false,
    isOptionsDirty: false
}

enum ResetType {
    Data = 1,
    Options = 2
}

enum ObjectDefaultsStateActionType {
    DataLoaded = 'DATA_LOADED',
    DefaultChanged = 'DEFAULT_CHANGED',
    ModeChanged = 'MODE_CHANGED',
    TatFilterValueChanged = 'TAT_FILTER_VALUE_CHANGED',
    Reset = 'OBJECT_DEFAULTS_RESET',
    ViewSelectedChanged = 'VIEW_SELECTED_CHANGED'
}

type ObjectDefaultsStateActions = | {
    type: ObjectDefaultsStateActionType.DataLoaded,
    payload: IObjectDefaultsModel | undefined
} | {
    type: ObjectDefaultsStateActionType.ModeChanged,
    payload: DefaultTagAssociationTypeEnum
} | {
    type: ObjectDefaultsStateActionType.TatFilterValueChanged,
    payload: string
} | {
    type: ObjectDefaultsStateActionType.Reset,
    payload: ResetType
} | {
    type: ObjectDefaultsStateActionType.DefaultChanged,
    payload: CheckListItemType
} | {
    type: ObjectDefaultsStateActionType.ViewSelectedChanged,
    payload: boolean
};

function isDirty(state: ObjectDefaultsStateType, copy: ObjectDefaultsStateType): boolean {
    const sortedDefaults = copy.defaults.sort((l, r) => l.defaultType === r.defaultType ? l.tagAssociationTypeID - r.tagAssociationTypeID : (l.defaultType as number) - (r.defaultType as number));
    return sortedDefaults.length !== state.initialDefaults.length ||
        !state.initialDefaults.every((v, i) => v.defaultType === sortedDefaults[i].defaultType && v.tagAssociationTypeID === sortedDefaults[i].tagAssociationTypeID);
}

function isOptionsDirty(copy: ObjectDefaultsStateType): boolean {
    return copy.options.mode !== defaultObjectDefaultsState.options.mode ||
        copy.options.tatsFilterValue !== defaultObjectDefaultsState.options.tatsFilterValue ||
        copy.options.viewSelected !== defaultObjectDefaultsState.options.viewSelected;
}

function objectDefaultsReducer(state: ObjectDefaultsStateType, action: ObjectDefaultsStateActions): ObjectDefaultsStateType {
    const copy = {
        ...state,
        availableTats: state.availableTats.map(o => { return {...o};}),
        defaults: state.defaults.map(o => { return {...o}; }),
        initialDefaults: state.initialDefaults.map(o => { return {...o}; }),
        options: {...state.options},
    };

    switch (action.type) {
        case ObjectDefaultsStateActionType.DataLoaded:
            copy.availableTats = ObjectDefaultUtils.getDefaultItems(action.payload);
            const sortedDefaults = action.payload?.defaults?.sort((l, r) => l.defaultType === r.defaultType ? l.tagAssociationTypeID - r.tagAssociationTypeID : (l.defaultType as number) - (r.defaultType as number)) ?? [];
            copy.initialDefaults = sortedDefaults;
            copy.defaults = copy.initialDefaults.map(o => {
                return {
                    ...o
                };
            });
            copy.isDirty = false;
            return copy;

        case ObjectDefaultsStateActionType.ModeChanged:
            copy.options.mode = action.payload;
            copy.isOptionsDirty = isOptionsDirty(copy);
            storageService.local.set(OBJECT_DEFAULTS_VIEW_OPTION_STORAGE_KEY, copy.options);
            return copy;

        case ObjectDefaultsStateActionType.TatFilterValueChanged:
            copy.options.tatsFilterValue = action.payload;
            copy.isOptionsDirty = isOptionsDirty(copy);
            storageService.local.set(OBJECT_DEFAULTS_VIEW_OPTION_STORAGE_KEY, copy.options);
            return copy;

        case ObjectDefaultsStateActionType.Reset:
            //console.log('Process reset: ', action.payload);
            if ((action.payload & ResetType.Data) === ResetType.Data) {
                copy.defaults = copy.initialDefaults.map(o => {
                    return {...o};
                });
                copy.availableTats = copy.availableTats.map(o => {
                    const parsed = o.key.split(':');
                    if (parsed.length > 1) {
                        const mode = Number(parsed[0]);
                        const tatID = Number(parsed[1]);
                        o.checked = copy.defaults.find(o => o.defaultType === mode as DefaultTagAssociationTypeEnum && o.tagAssociationTypeID === tatID) !== undefined;
                    }
                    return o;
                });
                copy.isDirty = false;
            }
            if ((action.payload & ResetType.Options) === ResetType.Options) {
                storageService.local.remove(OBJECT_DEFAULTS_VIEW_OPTION_STORAGE_KEY);
                copy.options = {...defaultObjectDefaultsState.options};
                copy.isOptionsDirty = false;
            }

            return copy;

        case ObjectDefaultsStateActionType.DefaultChanged:
            const availableTats = state.availableTats.map(currentItem => {
                return action.payload.key === currentItem.key
                    ? action.payload
                    : currentItem;
            });

            copy.availableTats = availableTats;

            const parsed = action.payload.key.split(':');
            if (parsed.length > 1) {
                const mode = Number(parsed[0]);
                const tatID = Number(parsed[1]);
                if (action.payload.checked) {
                    // add default
                    copy.defaults.push({
                        tagAssociationTypeID: tatID,
                        defaultType: mode
                    });
                } else {
                    // remove default
                    copy.defaults = copy.defaults.filter(o => o.defaultType !== mode || o.tagAssociationTypeID !== tatID);
                }
            }
            copy.isDirty = isDirty(state, copy);
            return copy;

        case ObjectDefaultsStateActionType.ViewSelectedChanged:
            copy.options.viewSelected = action.payload;
            copy.isOptionsDirty = isOptionsDirty(copy);
            storageService.local.set(OBJECT_DEFAULTS_VIEW_OPTION_STORAGE_KEY, copy.options);
            return copy;

        default:
            return state;
    }
}

export default function useObjectDefaultsState() {
    const [state, dispatch] = useReducer(objectDefaultsReducer, {...defaultObjectDefaultsState});

    const reset = useCallback(() => dispatch({
        type: ObjectDefaultsStateActionType.Reset,
        payload: ResetType.Data
    }), []);

    const resetOptions = useCallback(() => dispatch({
        type: ObjectDefaultsStateActionType.Reset,
        payload: ResetType.Options
    }), []);

    const setData = useCallback((data: IObjectDefaultsModel | undefined) => dispatch({
        type: ObjectDefaultsStateActionType.DataLoaded,
        payload: data
    }), []);

    const setMode = useCallback((mode: DefaultTagAssociationTypeEnum) => dispatch({
        type: ObjectDefaultsStateActionType.ModeChanged,
        payload: mode
    }), []);

    const setTatFilterValue = useCallback((filterValue: string) => dispatch({
        type: ObjectDefaultsStateActionType.TatFilterValueChanged,
        payload: filterValue
    }), []);

    const setViewSelected = useCallback((selected: boolean) => dispatch({
        type: ObjectDefaultsStateActionType.ViewSelectedChanged,
        payload: selected
    }), []);

    const updateTatDefault = useCallback((item: CheckListItemType) => dispatch({
        type: ObjectDefaultsStateActionType.DefaultChanged,
        payload: item
    }), []);


    return {
        state,
        reset,
        resetOptions,
        setData,
        setMode,
        setTatFilterValue,
        setViewSelected,
        updateTatDefault
    };
}